import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/ui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/use-toast';
import { ContentType, IntegrationV2Query, ProductIdentifier } from '@/gql/graphql';
import { useParams } from 'react-router';
import { getTypedMetadata, isSnapchatMetadata } from '@/routes/trackings/tracking-utils';
import { useEffect } from 'react';
import { useIntegrationStore } from '../../stores/tracking-store';
import { useApolloClient } from '@apollo/client';

import {
  FormLayout,
  FormSection,
  SectionHeader,
  RadioField,
  SectionParagraph
} from './../../components/tracking-details-form/base';
import { FormHeaderSection } from '../../components/tracking-details-form/shared-sections';
import { SaveButton } from '../../components/save-button';

const formSchema = z.object({
  tagDelivery: z.enum(['GTM', 'DIRECT']),
  transactionIdentifier: z.enum([
    ContentType.OrderId,
    ContentType.OrderName,
    ContentType.OrderNumber
  ]),
  productIdentifier: z.enum([
    ProductIdentifier.ProductId,
    ProductIdentifier.ProductSku,
    ProductIdentifier.VariantId
  ])
});

export const SnapchatProductSettingsTab = ({
  onContinue,
  isSetup
}: {
  onContinue: () => void;
  isSetup: boolean;
}) => {
  const { toast } = useToast();
  const params = useParams<{ id: string }>();
  const { integration, isLoading, updateMetadata } = useIntegrationStore();
  const client = useApolloClient();

  const metadata = getTypedMetadata({
    integrationV2: integration as IntegrationV2Query['integrationV2']
  });
  const snapchatMetadata = isSnapchatMetadata(metadata) ? metadata : null;

  // Check if the integration is fully configured
  const isConfigured = snapchatMetadata?.contentType && snapchatMetadata.productIdentifier;
  const isInitialSetup = !isConfigured || isSetup;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tagDelivery: 'GTM',
      transactionIdentifier: ContentType.OrderId,
      productIdentifier: ProductIdentifier.VariantId
    },
    resetOptions: {
      keepDirtyValues: true
    }
  });

  // Reset form when data loads
  useEffect(() => {
    if (snapchatMetadata) {
      // Use type assertion (nullish coalescing removed as snapchatMetadata is checked)
      const resetValues = {
        tagDelivery: (snapchatMetadata.tagDelivery as 'GTM' | 'DIRECT' | null | undefined) ?? 'GTM',
        transactionIdentifier: snapchatMetadata.contentType as
          | ContentType.OrderId
          | ContentType.OrderName
          | ContentType.OrderNumber,
        productIdentifier: snapchatMetadata.productIdentifier as
          | ProductIdentifier.ProductId
          | ProductIdentifier.ProductSku
          | ProductIdentifier.VariantId
      };
      form.reset(resetValues);
    }
  }, [snapchatMetadata, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await updateMetadata(client, {
        id: params.id!,
        metadata: {
          tagDelivery: values.tagDelivery,
          contentType: values.transactionIdentifier,
          productIdentifier: values.productIdentifier
        }
      });
      toast({
        title: 'Form submitted',
        description: 'Your Snapchat integration settings have been saved.',
        variant: 'default'
      });
      onContinue();
    } catch (_) {
      toast({
        title: 'Error',
        description: 'Your Snapchat integration settings could not be saved.',
        variant: 'destructive'
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Product Settings">
            This is where you can customize how we send product data to Snapchat.
          </FormHeaderSection>
          <FormSection>
            <SectionHeader>What is your Transaction Identifier?</SectionHeader>
            <SectionParagraph>
              Order ID is almost always used as the primary transaction identifier in Snapchat
              integrations, but you can override this if needed.
            </SectionParagraph>
            <RadioField
              form={form}
              name="transactionIdentifier"
              options={[
                {
                  label: 'Order ID',
                  value: ContentType.OrderId,
                  badge: 'Recommended'
                },
                {
                  label: 'Order Name',
                  value: ContentType.OrderName
                },
                {
                  label: 'Order Number',
                  value: ContentType.OrderNumber
                }
              ]}
            />
          </FormSection>
          <FormSection>
            <SectionHeader>What is your Product Identifier?</SectionHeader>
            <SectionParagraph>
              Variant ID is typically the primary identifier in Snapchat integrations, but this can
              be overridden if needed.
            </SectionParagraph>
            <RadioField
              form={form}
              name="productIdentifier"
              options={[
                {
                  label: 'Variant ID',
                  value: ProductIdentifier.VariantId,
                  badge: 'Recommended'
                },
                {
                  label: 'Product ID',
                  value: ProductIdentifier.ProductId
                },
                {
                  label: 'Product SKU',
                  value: ProductIdentifier.ProductSku
                }
              ]}
            />
          </FormSection>
          <FormSection>
            <SectionHeader>How should the browser pixel fire?</SectionHeader>
            <SectionParagraph>
              Through Google Tag Manager, you import Contextual&apos;s container into your own GTM
              account. Directly, Contextual fires the pixel from your storefront itself: no GTM
              account or container needed, and changes take effect within minutes.
            </SectionParagraph>
            <RadioField
              form={form}
              name="tagDelivery"
              options={[
                {
                  label: 'Through Google Tag Manager',
                  value: 'GTM'
                },
                {
                  label: 'Directly from Contextual',
                  value: 'DIRECT',
                  badge: 'No GTM needed'
                }
              ]}
            />
          </FormSection>
          <FormSection>
            <SaveButton isDirty={isDirty} isUpdating={isLoading} isInitialSetup={isInitialSetup} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};

export default SnapchatProductSettingsTab;
